import type { QueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import type { ReactionType } from "@/src/models/post.model";
import gqlClient from "@/src/services/api/graphql.client";
import { countersEnabled, recountPost } from "@/src/services/api/posts/engagement-functions";
import {
  DELETE_REACTION_BY_ID_MUTATION,
  GET_USER_REACTION_QUERY,
  INSERT_REACTION_MUTATION,
  UPDATE_POST_REACTION_COUNT_MUTATION,
  UPDATE_REACTION_MUTATION,
} from "@/src/services/api/posts/posts.query";
import { REACTIONS_QUERY_KEY } from "@/src/query-keys/post.keys";
import { patchCachedPost } from "./use-post-stickers";

/**
 * One reaction per user per post, with a single optimistic layer.
 *
 * While a user is reacting, the card shows `adjust(base, desired)`: the last
 * server-confirmed counts with exactly one change applied (old reaction out,
 * new one in). Taps only move `desired`; after a short quiet period one sync
 * writes the row to match `desired`, asks the server to recount, and — only if
 * nothing changed meanwhile — hands the server's numbers to the query cache and
 * drops the override. A tap during a sync just loops once more, so a stale
 * response is never shown.
 */
export type Counts = Partial<Record<Exclude<ReactionType, null>, number>>;

type Entry = {
  baseType: ReactionType | null;
  baseCounts: Counts;
  startType: ReactionType | null;
  desired: ReactionType | null;
  timer?: ReturnType<typeof setTimeout>;
  syncing: boolean;
};

export const REACTION_DEBOUNCE_MS = 400;

const entries = new Map<string, Entry>();
const listeners = new Set<() => void>();
let version = 0;
const emit = () => { version++; listeners.forEach((l) => l()); };
// Published views are immutable snapshots, so useSyncExternalStore hands React a new
// value on every change. (The React Compiler memoises plain calls by their inputs, so a
// view computed from module state outside the snapshot would never re-render.)
const views = new Map<string, ReactionView>();
const publish = (key: string) => {
  const e = entries.get(key);
  if (e) views.set(key, { type: e.desired, counts: adjustCounts(e.baseCounts, e.baseType, e.desired), pending: true });
  else views.delete(key);
  emit();
};
const keyOf = (postId: string, userId: string) => `${postId}|${userId}`;

export const adjustCounts = (counts: Counts, from: ReactionType | null, to: ReactionType | null): Counts => {
  const next: Counts = { ...counts };
  if (from === to) return next;
  if (from) next[from] = Math.max(0, (next[from] ?? 0) - 1);
  if (to) next[to] = (next[to] ?? 0) + 1;
  return next;
};

export type ReactionView = { type: ReactionType | null; counts: Counts; pending: boolean };

export const reactionView = (postId: string, userId: string, serverType: ReactionType | null, serverCounts: Counts): ReactionView => {
  const e = entries.get(keyOf(postId, userId));
  if (!e) return { type: serverType, counts: serverCounts ?? {}, pending: false };
  return { type: e.desired, counts: adjustCounts(e.baseCounts, e.baseType, e.desired), pending: true };
};

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

export const useReactionView = (postId: string, userId: string, serverType: ReactionType | null, serverCounts: Counts): ReactionView => {
  const key = keyOf(postId, userId);
  const snap = useSyncExternalStore(subscribe, () => views.get(key), () => views.get(key));
  return snap ?? { type: serverType, counts: serverCounts ?? {}, pending: false };
};

export type ReactArgs = {
  queryClient: QueryClient;
  postId: string;
  userId: string;
  serverType: ReactionType | null;
  serverCounts: Counts;
  next: ReactionType | null;
  reactorName?: string;
  reactorProfileImageUrl?: string;
  /** Once per burst, after the server has the final state. */
  onCommitted?: (final: ReactionType | null, start: ReactionType | null, counts: Counts) => void;
  onFailed?: (error: unknown) => void;
  debounceMs?: number;
};

/** Writes the user's post reaction row so it matches `target`. Returns the row id. */
export const writeReaction = async (postId: string, userId: string, target: ReactionType | null, extra: { reactorName?: string; reactorProfileImageUrl?: string }) => {
  const client = await gqlClient();
  const res = await client.request(GET_USER_REACTION_QUERY, { where: { postId: { eq: postId }, userId: { eq: userId } } });
  // Comment reactions carry postId as well; the post reaction is the row without commentId.
  const rows: any[] = (res.getreactions?.items ?? []).filter((r: any) => r && !r.commentId);
  const [keep, ...dupes] = rows;
  for (const d of dupes) await client.request(DELETE_REACTION_BY_ID_MUTATION, { itemId: d.ItemId });
  if (!target) {
    if (keep) await client.request(DELETE_REACTION_BY_ID_MUTATION, { itemId: keep.ItemId });
    return null;
  }
  if (keep) {
    if (keep.type !== target) await client.request(UPDATE_REACTION_MUTATION, { itemId: keep.ItemId, type: target });
    return keep.ItemId as string;
  }
  const ins = await client.request(INSERT_REACTION_MUTATION, { userId, postId, type: target, ...extra });
  return ins.insertreaction?.itemId ?? null;
};

const sync = async (key: string, a: ReactArgs) => {
  const e = entries.get(key);
  if (!e || e.syncing) return;
  e.syncing = true;
  try {
    let id: string | null = null;
    let counts: Counts = e.baseCounts;
    for (;;) {
      const target = e.desired;
      id = await writeReaction(a.postId, a.userId, target, { reactorName: a.reactorName, reactorProfileImageUrl: a.reactorProfileImageUrl });
      if (countersEnabled) {
        const server = await recountPost(a.postId);
        counts = server?.reactionCount ? { ...server.reactionCount } : adjustCounts(e.baseCounts, e.baseType, target);
      } else {
        counts = adjustCounts(e.baseCounts, e.baseType, target);
        try {
          const client = await gqlClient();
          await client.request(UPDATE_POST_REACTION_COUNT_MUTATION, { itemId: a.postId, reactionCount: counts });
        } catch { /* legacy count write is best effort */ }
      }
      e.baseType = target;
      e.baseCounts = counts;
      publish(key);
      if (e.desired === target) break; // only the latest state is applied
    }
    const final = e.desired;
    // Hand the confirmed state to the cache first, then drop the override: no flicker.
    patchCachedPost(a.queryClient, a.postId, (p) => ({ ...p, reactionCount: { ...(p.reactionCount ?? {}), ...counts } }));
    a.queryClient.setQueryData([REACTIONS_QUERY_KEY, a.postId, a.userId], { type: final, id });
    entries.delete(key);
    publish(key);
    a.queryClient.invalidateQueries({ queryKey: [REACTIONS_QUERY_KEY, a.postId], exact: true });
    a.onCommitted?.(final, e.startType, counts);
  } catch (error) {
    entries.delete(key);
    publish(key);
    a.queryClient.invalidateQueries({ queryKey: [REACTIONS_QUERY_KEY, a.postId, a.userId] });
    a.onFailed?.(error);
  }
};

/** Selects `next` (null removes). Debounced; safe to call on every tap. */
export const react = (a: ReactArgs) => {
  const key = keyOf(a.postId, a.userId);
  let e = entries.get(key);
  if (!e) {
    e = { baseType: a.serverType, baseCounts: { ...(a.serverCounts ?? {}) }, startType: a.serverType, desired: a.serverType, syncing: false };
    entries.set(key, e);
  }
  e.desired = a.next;
  publish(key);
  if (e.timer) clearTimeout(e.timer);
  e.timer = setTimeout(() => { e!.timer = undefined; void sync(key, a); }, a.debounceMs ?? REACTION_DEBOUNCE_MS);
};

/** Test helper. */
export const __resetReactionSync = () => { entries.forEach((e) => e.timer && clearTimeout(e.timer)); entries.clear(); views.clear(); emit(); };
